import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import SafetyShell from "../safety/SafetyShell";
import {
  FileUp,
  Search,
  Download,
  Trash2,
  AlertTriangle,
  Package,
  FileSpreadsheet,
  CheckCircle2,
  RefreshCw,
  ListChecks,
  Ship,
  Link2,
  Tags,
  IndianRupee,
} from "lucide-react";
import DomesticSales from "./DomesticSales";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function PackingLists({ user, onLogout }) {
  const [permissions, setPermissions] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const fileInputRef = useRef(null);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState("export");

  const has = (p) => permissions.includes(p);
  const canView = has("packing_list.view");
  const canUpload = has("packing_list.upload");
  const canDownload = has("packing_list.download");
  const canDelete = has("packing_list.delete");
  const canParse = has("inventory.manage");
  const canLinkPo = has("po.line_item.edit") || has("po.upload") || has("po.replace");
  const canManageCust = has("customer.manage");

  // Bulk reparse + per-row line viewer
  const [bulkBusy, setBulkBusy] = useState(false);
  const [linesDialog, setLinesDialog] = useState({ open: false, row: null, data: null, loading: false });
  const [rowBusyId, setRowBusyId] = useState(null);
  const [linkHealth, setLinkHealth] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerOpts, setCustomerOpts] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [assignDialog, setAssignDialog] = useState({ open: false, row: null, customerId: "", newName: "" });
  const [assignBusy, setAssignBusy] = useState(false);

  const loadCustomerData = async () => {
    try {
      const [opts, list] = await Promise.all([
        axios.get(`${API}/packing-lists/customer-options`, getAuthHeaders()),
        axios.get(`${API}/customers`, getAuthHeaders()),
      ]);
      setCustomerOpts(opts.data?.customers || []);
      setCustomersList(list.data?.customers || list.data || []);
    } catch (_) { /* ignore */ }
  };

  const submitAssign = async () => {
    const raw = assignDialog.row?.customer_name;
    if (!raw) return;
    setAssignBusy(true);
    try {
      if (assignDialog.customerId === "__new__") {
        const name = (assignDialog.newName || "").trim();
        if (!name) { toast.error("Enter a customer name"); setAssignBusy(false); return; }
        await axios.post(`${API}/customers`, { name, aliases: name.toUpperCase() === raw.toUpperCase() ? [] : [raw] }, getAuthHeaders());
      } else if (assignDialog.customerId) {
        await axios.post(`${API}/customers/${assignDialog.customerId}/aliases`, { alias: raw }, getAuthHeaders());
      } else {
        toast.error("Pick a customer"); setAssignBusy(false); return;
      }
      toast.success("Customer grouping updated");
      setAssignDialog({ open: false, row: null, customerId: "", newName: "" });
      await Promise.all([fetchList(), loadCustomerData()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update grouping");
    } finally {
      setAssignBusy(false);
    }
  };

  const scanLinkHealth = async () => {
    setScanBusy(true);
    try {
      const r = await axios.get(`${API}/packing-lists/link-health`, getAuthHeaders());
      const d = r.data || {};
      setLinkHealth(d);
      if ((d.stale_count || 0) === 0) {
        toast.success("All packing lists are linked correctly — no PO gaps found.");
      } else {
        toast.warning(`${d.stale_count} packing list(s) have PO linkage gaps — click "Fix multi-PO invoices" to repair.`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Linkage scan failed");
    } finally {
      setScanBusy(false);
    }
  };

  const handleBulkReparse = async (forceAll = false) => {
    const msg = forceAll
      ? "Force re-parse ALL packing lists (including already-parsed)? This is needed after the parser logic has been updated. Existing dispatch lines will be replaced."
      : "Re-parse only previously-unparsed packing lists?";
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(
        `${API}/packing-lists/bulk-reparse?only_unparsed=${forceAll ? "false" : "true"}`,
        {},
        getAuthHeaders(),
      );
      const d = r.data || {};
      toast.success(
        `Re-parse done · processed=${d.processed} · succeeded=${d.succeeded} · skipped=${d.skipped} · failed=${d.failed}`,
        { duration: 7000 },
      );
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk re-parse failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBackfillPoNumbers = async () => {
    if (!window.confirm("Re-scan all packing-list files to recover multi-PO invoices (e.g. one invoice covering '20299, 20321')? This updates PO linkage used by the Management Report.")) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/packing-lists/backfill-po-numbers`, {}, getAuthHeaders());
      const d = r.data || {};
      toast.success(
        `Backfill done · processed=${d.processed} · updated=${d.updated} · multi-PO invoices=${d.multi_po} · failed=${d.failed}`,
        { duration: 8000 },
      );
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBulkBusy(false);
    }
    // refresh the linkage health chip after a fix
    if (linkHealth) scanLinkHealth();
  };

  const handleRowReparse = async (row) => {
    setRowBusyId(row.id);
    try {
      const r = await axios.post(`${API}/packing-lists/${row.id}/parse-lines`, {}, getAuthHeaders());
      const d = r.data || {};
      if (d.ok) {
        toast.success(`Parsed ${d.lines} lines${(d.warnings || []).length ? ` · ${(d.warnings || []).length} warning(s)` : ""}`);
      } else {
        toast.warning(d.reason ? `Skipped: ${d.reason}` : "No lines extracted");
      }
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-parse failed");
    } finally {
      setRowBusyId(null);
    }
  };

  const handleViewLines = async (row) => {
    setLinesDialog({ open: true, row, data: null, loading: true });
    try {
      const r = await axios.get(`${API}/packing-lists/${row.id}/lines`, getAuthHeaders());
      setLinesDialog({ open: true, row, data: r.data, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load lines");
      setLinesDialog({ open: false, row: null, data: null, loading: false });
    }
  };

  // --- Link a mismatched PO number to an existing PO (alias) ---
  const [linkDialog, setLinkDialog] = useState({ open: false, row: null });
  const [poQuery, setPoQuery] = useState("");
  const [poResults, setPoResults] = useState([]);
  const [poSearching, setPoSearching] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const unmatchedCount = items.filter((i) => i.po_has_number && !i.po_matched).length;
  const displayItems = unmatchedOnly ? items.filter((i) => i.po_has_number && !i.po_matched) : items;

  const plNumbers = (row) =>
    ((row?.po_numbers && row.po_numbers.length ? row.po_numbers : [row?.po_number]) || []).filter(Boolean);

  const openLinkDialog = (row) => {
    setLinkDialog({ open: true, row });
    setPoQuery("");
    setPoResults([]);
  };

  const searchPos = async (q) => {
    setPoQuery(q);
    if (!q || !q.trim()) {
      setPoResults([]);
      return;
    }
    setPoSearching(true);
    try {
      const r = await axios.get(`${API}/po/list?search=${encodeURIComponent(q.trim())}`, getAuthHeaders());
      setPoResults((r.data || []).slice(0, 20));
    } catch (err) {
      toast.error(err.response?.data?.detail || "PO search failed");
    } finally {
      setPoSearching(false);
    }
  };

  const doLink = async (po) => {
    const row = linkDialog.row;
    const nums = plNumbers(row);
    if (!nums.length) {
      toast.error("This packing list has no PO number to link");
      return;
    }
    setLinkBusy(true);
    try {
      for (const num of nums) {
        await axios.post(`${API}/po/${po.id}/aliases`, { alias: num }, getAuthHeaders());
      }
      toast.success(`Linked ${nums.join(", ")} → PO ${po.po_number}. The Management Report will match automatically.`, { duration: 7000 });
      setLinkDialog({ open: false, row: null });
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link PO");
    } finally {
      setLinkBusy(false);
    }
  };

  // --- Edit the PO number(s) recorded on a packing list ---
  const [editPoDialog, setEditPoDialog] = useState({ open: false, row: null });
  const [editNums, setEditNums] = useState([]);
  const [newNum, setNewNum] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const openEditPo = (row) => {
    setEditNums([...plNumbers(row)]);
    setNewNum("");
    setEditPoDialog({ open: true, row });
  };
  const addNum = () => {
    const s = newNum.trim();
    if (!s) return;
    if (editNums.some((n) => n.toUpperCase() === s.toUpperCase())) {
      setNewNum("");
      return;
    }
    setEditNums((prev) => [...prev, s]);
    setNewNum("");
  };
  const removeNum = (n) => setEditNums((prev) => prev.filter((x) => x !== n));
  const saveEditPo = async () => {
    setEditBusy(true);
    try {
      await axios.put(`${API}/packing-lists/${editPoDialog.row.id}/po-numbers`, { po_numbers: editNums }, getAuthHeaders());
      toast.success("PO number(s) updated. The Management Report will reflect this.");
      setEditPoDialog({ open: false, row: null });
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update PO numbers");
    } finally {
      setEditBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
        setPermissions(r.data.effective_permissions || []);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!canView && permissions.length > 0) {
      setLoading(false);
      return;
    }
    fetchList();
    loadCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, customerFilter, permissions.length]);

  const fetchList = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (customerFilter) params.set("customer", customerFilter);
      const qs = params.toString();
      const url = qs ? `${API}/packing-lists?${qs}` : `${API}/packing-lists`;
      const r = await axios.get(url, getAuthHeaders());
      setItems(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load packing lists");
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = [".xls", ".xlsx", ".xlsm", ".pdf"].some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!ok) {
      toast.error("Only .xls, .xlsx, .xlsm or .pdf files are supported");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (note.trim()) fd.append("note", note.trim());
      const r = await axios.post(`${API}/packing-lists/upload`, fd, {
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      const data = r.data;
      if (data.invoice_no) {
        toast.success(`Uploaded · ${data.invoice_no}${data.po_number ? ` · PO ${data.po_number}` : ""}`);
      } else {
        toast.success("Uploaded — invoice details not auto-detected. You can still find it by filename.");
      }
      setNote("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (row) => {
    try {
      const res = await axios.get(`${API}/packing-lists/${row.id}/download`, getAuthHeaders());
      const url = res.data?.url;
      if (!url) {
        // Local-storage fallback returns a stream, not JSON; in that case axios
        // will have parsed it differently — just open the endpoint in a new tab.
        window.open(`${API}/packing-lists/${row.id}/download`, "_blank");
        return;
      }
      // Trigger a download via a hidden anchor — works cross-origin without CORS
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = row.file_name || "packing_list";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.row) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/packing-lists/${deleteDialog.row.id}`, getAuthHeaders());
      toast.success("Deleted");
      setDeleteDialog({ open: false, row: null });
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Invoice cum Packing Lists" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
        { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
        { label: "Invoice cum Packing Lists", active: true, depth: 1 },
        { label: "Customers", to: "/safety/customers", depth: 1 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="packing-lists-page">
        {/* Header */}
        <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-orange-600" />
            <div>
              <h1 className="text-xl font-bold tracking-tight">Packing Lists & Invoices</h1>
              <p className="text-xs text-zinc-500">
                Upload and archive commercial-invoice / packing-list files. Stored on R2 — full data parsing happens later as
                modules need it.
              </p>
            </div>
          </div>
          {tab === "export" && (<>
          {canParse && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkReparse(false)}
                disabled={bulkBusy}
                data-testid="pl-bulk-reparse-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${bulkBusy ? "animate-spin" : ""}`} />
                {bulkBusy ? "Working…" : "Re-parse unparsed"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkReparse(true)}
                disabled={bulkBusy}
                title="Force re-parse every packing list with the latest parser (use after a parser update or schema change)"
                data-testid="pl-force-reparse-btn"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${bulkBusy ? "animate-spin" : ""}`} />
                Force re-parse ALL
              </Button>
            </div>
          )}
          {canUpload && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackfillPoNumbers}
              disabled={bulkBusy}
              title="Re-scan files to recover invoices that cover multiple POs (e.g. '20299, 20321') so the Management Report links dispatch to every PO."
              data-testid="pl-backfill-po-btn"
              className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${bulkBusy ? "animate-spin" : ""}`} />
              Fix multi-PO invoices
            </Button>
          )}
          {canView && (
            <Button
              variant="outline"
              size="sm"
              onClick={scanLinkHealth}
              disabled={scanBusy}
              title="Scan all packing-list files for PO-linkage gaps (legacy files that captured only some of a multi-PO invoice's POs)."
              data-testid="pl-scan-linkage-btn"
            >
              <Link2 className={`w-4 h-4 mr-1.5 ${scanBusy ? "animate-pulse" : ""}`} />
              {scanBusy ? "Scanning…" : "Scan linkage"}
            </Button>
          )}
          </>)}
        </div>

        {/* Tab switcher: Export vs Domestic (INR) */}
        <div className="flex items-center gap-1 mb-4 border-b" data-testid="pl-tabs">
          <button
            onClick={() => setTab("export")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "export" ? "border-orange-600 text-orange-700" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}
            data-testid="pl-tab-export"
          >
            <Package className="w-4 h-4 inline mr-1.5 -mt-0.5" />Export Invoices / Packing Lists
          </button>
          <button
            onClick={() => setTab("domestic")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "domestic" ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}
            data-testid="pl-tab-domestic"
          >
            <IndianRupee className="w-4 h-4 inline mr-1.5 -mt-0.5" />Domestic Sales (INR)
          </button>
        </div>

        {tab === "domestic" && <DomesticSales permissions={permissions} />}

        {tab === "export" && (<>

        {/* Linkage health chip */}
        {linkHealth && (linkHealth.stale_count > 0 ? (
          <div
            className="flex items-center justify-between gap-3 mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5"
            data-testid="pl-link-health-warning"
          >
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                <b data-testid="pl-link-health-count">{linkHealth.stale_count}</b> packing list{linkHealth.stale_count > 1 ? "s" : ""} may have unlinked POs
                {linkHealth.empty_invoice_count > 0 ? ` · ${linkHealth.empty_invoice_count} missing invoice no.` : ""}
                {" "}— dispatch won't map to every PO in the Management Report until fixed.
              </span>
            </div>
            {canUpload && (
              <Button
                size="sm"
                onClick={handleBackfillPoNumbers}
                disabled={bulkBusy}
                data-testid="pl-link-health-fix-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${bulkBusy ? "animate-spin" : ""}`} />
                Fix now
              </Button>
            )}
          </div>
        ) : (
          <div
            className="flex items-center gap-2 mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700"
            data-testid="pl-link-health-ok"
          >
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>All {linkHealth.total} packing lists are linked correctly — no PO gaps found.</span>
          </div>
        ))}

        {/* Upload section */}
        {canUpload && (
          <div className="sf-surface p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FileUp className="w-4 h-4 text-zinc-500" />
              <span className="font-medium text-sm">Upload New Packing List / Invoice</span>
            </div>
            <div className="grid md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <Label className="text-xs text-zinc-500">Optional note (e.g. shipment ref, batch description)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. May export — Container MRKU0969450"
                  data-testid="pl-note-input"
                />
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xls,.xlsx,.xlsm,.pdf"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="pl-file-input"
                />
                <Button
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="pl-upload-btn"
                >
                  {uploading ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <FileUp className="w-4 h-4 mr-2" />
                      Choose file & upload
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Supported: .xls .xlsx .xlsm .pdf · Max 50 MB · We auto-detect Invoice No, PO, Date, Customer & Container from the file.
            </p>
          </div>
        )}

        {/* Search + List */}
        <div className="sf-surface p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search invoice no, PO, customer, filename, container…"
                className="pl-9"
                data-testid="pl-search"
              />
            </div>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm max-w-[240px]"
              data-testid="pl-customer-filter"
              title="Filter by customer (location variants are grouped into one)"
            >
              <option value="">All customers</option>
              {customerOpts.map((o) => (
                <option key={o.label} value={o.label}>{o.label} ({o.count})</option>
              ))}
            </select>
            {unmatchedCount > 0 && (
              <button
                type="button"
                onClick={() => setUnmatchedOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                  unmatchedOnly
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                }`}
                title="Show only packing lists whose PO number matches no existing PO"
                data-testid="pl-unmatched-filter"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {unmatchedOnly ? "Showing unmatched" : `${unmatchedCount} unmatched PO${unmatchedCount === 1 ? "" : "s"}`}
              </button>
            )}
            <span className="text-xs text-zinc-500 ml-auto" data-testid="pl-record-count">{displayItems.length} record{displayItems.length === 1 ? "" : "s"}</span>
          </div>

          <div className="overflow-x-auto rounded border bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto" />
              </div>
            ) : !canView ? (
              <div className="p-8 text-center text-zinc-500 text-sm">You do not have permission to view packing lists.</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center text-zinc-500">
                <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No packing lists uploaded yet</p>
                {canUpload && <p className="text-xs">Use the upload box above to get started</p>}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Invoice No</th>
                    <th className="px-3 py-2 text-left font-medium">PO</th>
                    <th className="px-3 py-2 text-left font-medium">Date</th>
                    <th className="px-3 py-2 text-left font-medium">Customer</th>
                    <th className="px-3 py-2 text-right font-medium">Total Qty</th>
                    <th className="px-3 py-2 text-left font-medium">Container</th>
                    <th className="px-3 py-2 text-left font-medium">Shipping Bill</th>
                    <th className="px-3 py-2 text-left font-medium">File</th>
                    <th className="px-3 py-2 text-left font-medium">Uploaded</th>
                    <th className="px-3 py-2 text-right font-medium w-44">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((row) => (
                    <tr key={row.id} className="border-t hover:bg-zinc-50" data-testid={`pl-row-${row.id}`}>
                      <td className="px-3 py-2 font-mono">
                        {row.invoice_no || <span className="text-zinc-400">—</span>}
                        {row.duplicate_of && (
                          <span title="Duplicate invoice number" className="ml-2 inline-flex items-center text-amber-600 text-xs">
                            <AlertTriangle className="w-3 h-3 mr-0.5" /> dup
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        {row.po_number ? (
                          <span className="inline-flex items-center gap-1.5">
                            {row.po_number}
                            {row.po_has_number && !row.po_matched && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                                title="This PO number matches no existing PO. Use the Link PO action to map it to the correct PO."
                                data-testid={`pl-unmatched-badge-${row.id}`}
                              >
                                <AlertTriangle className="w-2.5 h-2.5" /> unmatched
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">{row.invoice_date || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 max-w-[220px]">
                        <div className="flex items-center gap-1.5 group">
                          <span className="truncate" title={row.customer_name || ""}>
                            {row.customer_name || <span className="text-zinc-400">—</span>}
                          </span>
                          {row.customer_name && canManageCust && (
                            <button
                              type="button"
                              onClick={() => setAssignDialog({ open: true, row, customerId: "", newName: row.customer_name })}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-orange-600 shrink-0"
                              title="Group this customer (merge location variants into one)"
                              data-testid={`pl-assign-customer-${row.id}`}
                            >
                              <Tags className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        {row.customer_canonical && row.customer_canonical.toUpperCase() !== (row.customer_name || "").toUpperCase() && (
                          <span className="block text-[10px] text-zinc-400 truncate" title={`Grouped as ${row.customer_canonical}`}>↳ {row.customer_canonical}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{row.total_qty ?? <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.container_no || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2">
                        {row.shipping_bill ? (
                          <a
                            href={`${API}/shipping-bills/${row.shipping_bill.id}/preview?token=${encodeURIComponent(localStorage.getItem("token"))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100"
                            title={`View Shipping Bill ${row.shipping_bill.sb_no || ""}`}
                            data-testid={`pl-shipping-bill-${row.id}`}
                          >
                            <Ship className="w-3 h-3" /> {row.shipping_bill.sb_no || "View"}
                          </a>
                        ) : (
                          <span className="text-zinc-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="truncate max-w-[200px]" title={row.file_name}>{row.file_name}</div>
                        <div className="text-[11px] text-zinc-500">{formatBytes(row.file_size)}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600">
                        <div>{row.uploaded_at ? new Date(row.uploaded_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</div>
                        <div className="text-zinc-500">{row.uploaded_by}</div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canLinkPo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-zinc-600 hover:text-zinc-800"
                              onClick={() => openEditPo(row)}
                              title="Edit the PO number(s) recorded on this packing list"
                              data-testid={`pl-edit-po-${row.id}`}
                            >
                              <Tags className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canLinkPo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-indigo-600 hover:text-indigo-700"
                              onClick={() => openLinkDialog(row)}
                              title="Link this packing list's PO number to an existing PO (fix mismatched numbers)"
                              data-testid={`pl-link-po-${row.id}`}
                            >
                              <Link2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canParse && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => handleViewLines(row)}
                              title="View / extract dispatch lines"
                              data-testid={`pl-lines-${row.id}`}
                            >
                              <ListChecks className="w-3.5 h-3.5 mr-0.5" />
                              {row.parse_status === "parsed_dispatch" ? (row.parsed_line_count ?? "—") : "Parse"}
                            </Button>
                          )}
                          {canDownload && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleDownload(row)}
                              title="Download original"
                              data-testid={`pl-download-${row.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                              onClick={() => setDeleteDialog({ open: true, row })}
                              title="Delete"
                              data-testid={`pl-delete-${row.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Phase note */}
          {items.length > 0 && (
            <div className="mt-3 flex items-start gap-2 text-xs text-zinc-500">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
              <span>
                Files are archived on Cloudflare R2. Click the line-count button on any row to view (or trigger) dispatch line
                extraction — these lines feed the Inventory ledger automatically.
              </span>
            </div>
          )}
        </div>
        </>)}
      </div>

      {/* Delete confirm dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && !deleting && setDeleteDialog({ open: false, row: null })}>
        <DialogContent data-testid="pl-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Delete packing list?
            </DialogTitle>
            <DialogDescription>
              {deleteDialog.row?.invoice_no || deleteDialog.row?.file_name}<br />
              The original file on R2 will be removed. This action is logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, row: null })} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="pl-delete-confirm">
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link PO number → existing PO (alias) dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(o) => !o && !linkBusy && setLinkDialog({ open: false, row: null })}>
        <DialogContent className="max-w-lg" data-testid="pl-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-indigo-600" /> Link PO number
            </DialogTitle>
            <DialogDescription>
              This packing list references PO&nbsp;
              <span className="font-mono font-semibold text-zinc-800">{plNumbers(linkDialog.row).join(", ") || "—"}</span>.
              If that number is a typo/variant of an existing PO, pick the correct PO below — it will be saved as an
              alternate number so this (and future) packing lists &amp; shipping bills link automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={poQuery}
                onChange={(e) => searchPos(e.target.value)}
                placeholder="Search PO number or vendor…"
                className="pl-9"
                autoFocus
                data-testid="pl-link-po-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto rounded border divide-y">
              {poSearching ? (
                <div className="p-4 text-center text-sm text-zinc-500">Searching…</div>
              ) : poResults.length === 0 ? (
                <div className="p-4 text-center text-sm text-zinc-400">
                  {poQuery ? "No matching POs" : "Type to search for the correct PO"}
                </div>
              ) : (
                poResults.map((po) => (
                  <button
                    key={po.id}
                    type="button"
                    disabled={linkBusy}
                    onClick={() => doLink(po)}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center justify-between gap-2 disabled:opacity-50"
                    data-testid={`pl-link-po-option-${po.po_number}`}
                  >
                    <div>
                      <div className="font-mono font-semibold text-sm text-zinc-800">{po.po_number}</div>
                      <div className="text-xs text-zinc-500 truncate max-w-[280px]">
                        {po.vendor_name || po.customer_name || "—"}
                        {(po.po_number_aliases || []).length > 0 && (
                          <span className="ml-1 text-indigo-500">· alt: {(po.po_number_aliases || []).join(", ")}</span>
                        )}
                      </div>
                    </div>
                    <Link2 className="w-4 h-4 text-indigo-500 shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, row: null })} disabled={linkBusy}>
              {linkBusy ? "Linking…" : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PO numbers dialog */}
      <Dialog open={editPoDialog.open} onOpenChange={(o) => !o && !editBusy && setEditPoDialog({ open: false, row: null })}>
        <DialogContent className="max-w-md" data-testid="pl-edit-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tags className="w-5 h-5 text-zinc-600" /> Edit PO numbers
            </DialogTitle>
            <DialogDescription>
              PO number(s) recorded on invoice{" "}
              <span className="font-mono">{editPoDialog.row?.invoice_no || editPoDialog.row?.file_name}</span>.
              Remove any that don't belong (e.g. a PO cross-attached from a multi-invoice file) — this controls which PO
              the Management Report links this invoice to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 min-h-[2rem]">
              {editNums.length === 0 ? (
                <span className="text-xs text-zinc-400">No PO numbers</span>
              ) : (
                editNums.map((n) => (
                  <span
                    key={n}
                    className="inline-flex items-center gap-1.5 text-sm font-mono px-2 py-1 rounded bg-zinc-100 border"
                    data-testid={`pl-edit-po-chip-${n}`}
                  >
                    {n}
                    <button
                      type="button"
                      onClick={() => removeNum(n)}
                      className="text-red-500 hover:text-red-700 text-base leading-none"
                      title="Remove"
                      data-testid={`pl-edit-po-remove-${n}`}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={newNum}
                onChange={(e) => setNewNum(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addNum();
                  }
                }}
                placeholder="Add a PO number…"
                data-testid="pl-edit-po-input"
              />
              <Button variant="outline" onClick={addNum} data-testid="pl-edit-po-add">Add</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPoDialog({ open: false, row: null })} disabled={editBusy}>
              Cancel
            </Button>
            <Button onClick={saveEditPo} disabled={editBusy} data-testid="pl-edit-po-save">
              {editBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lines / Re-parse dialog */}
      <Dialog open={linesDialog.open} onOpenChange={(o) => !o && setLinesDialog({ open: false, row: null, data: null, loading: false })}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="pl-lines-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-emerald-600" />
              Dispatch lines · {linesDialog.row?.invoice_no || linesDialog.row?.file_name}
            </DialogTitle>
            <DialogDescription>
              These lines feed the Inventory dispatch ledger. Re-parse anytime if the original file has been updated.
            </DialogDescription>
          </DialogHeader>
          {linesDialog.loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto" />
            </div>
          ) : linesDialog.data ? (
            <div>
              <div className="flex items-center gap-2 mb-3 flex-wrap text-xs">
                <span className="px-2 py-1 rounded bg-zinc-100 text-zinc-700">Status: {linesDialog.data.parse_status || "—"}</span>
                <span className="px-2 py-1 rounded bg-zinc-100 text-zinc-700">Lines: {linesDialog.data.parsed_line_count ?? 0}</span>
                {linesDialog.data.last_parsed_at && (
                  <span className="px-2 py-1 rounded bg-zinc-100 text-zinc-700">
                    Last parsed: {new Date(linesDialog.data.last_parsed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                  </span>
                )}
                {canParse && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 ml-auto"
                    onClick={() => handleRowReparse(linesDialog.row)}
                    disabled={rowBusyId === linesDialog.row?.id}
                    data-testid="pl-lines-reparse-btn"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${rowBusyId === linesDialog.row?.id ? "animate-spin" : ""}`} />
                    Re-parse this file
                  </Button>
                )}
              </div>
              {(linesDialog.data.parse_warnings || []).length > 0 && (
                <div className="text-xs text-amber-700 mb-3 p-2 bg-amber-50 rounded border border-amber-200">
                  <div className="font-medium mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Parse warnings
                  </div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {linesDialog.data.parse_warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <div className="overflow-x-auto rounded border bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">#</th>
                      <th className="px-2 py-1.5 text-left font-medium">Product code</th>
                      <th className="px-2 py-1.5 text-left font-medium">Product name</th>
                      <th className="px-2 py-1.5 text-left font-medium">PO</th>
                      <th className="px-2 py-1.5 text-left font-medium">Pallet</th>
                      <th className="px-2 py-1.5 text-right font-medium">Pieces</th>
                      <th className="px-2 py-1.5 text-right font-medium">Kg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(linesDialog.data.items || []).map((ln) => (
                      <tr key={ln.line_no} className="border-t">
                        <td className="px-2 py-1 text-zinc-500">{ln.line_no}</td>
                        <td className="px-2 py-1 font-mono">{ln.product_code}</td>
                        <td className="px-2 py-1 truncate max-w-[260px]" title={ln.product_name}>{ln.product_name || "—"}</td>
                        <td className="px-2 py-1 font-mono">{ln.po_number || <span className="italic text-zinc-400">—</span>}</td>
                        <td className="px-2 py-1 font-mono">{ln.pallet_no || <span className="italic text-zinc-400">—</span>}</td>
                        <td className="px-2 py-1 text-right">{ln.qty_pieces?.toLocaleString()}</td>
                        <td className="px-2 py-1 text-right text-zinc-500">{ln.qty_kg ?? "—"}</td>
                      </tr>
                    ))}
                    {(linesDialog.data.items || []).length === 0 && (
                      <tr><td colSpan={7} className="p-4 text-center text-zinc-400 italic">
                        No lines yet. Click "Re-parse this file" above to extract.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Assign / group customer */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => !o && !assignBusy && setAssignDialog({ open: false, row: null, customerId: "", newName: "" })}>
        <DialogContent data-testid="pl-assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Tags className="w-4 h-4 text-orange-600" /> Group customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-600">Assign this consignee name to a single master customer so all its location variants are treated as one in filters.</p>
            <div className="bg-zinc-50 border rounded px-3 py-2 text-xs font-mono break-words" data-testid="pl-assign-raw">{assignDialog.row?.customer_name}</div>
            <label className="block text-xs font-medium text-zinc-500">Assign to customer</label>
            <select
              value={assignDialog.customerId}
              onChange={(e) => setAssignDialog((d) => ({ ...d, customerId: e.target.value }))}
              className="w-full h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm"
              data-testid="pl-assign-select"
            >
              <option value="">— Select —</option>
              {customersList.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value="__new__">➕ Create new customer…</option>
            </select>
            {assignDialog.customerId === "__new__" && (
              <Input
                value={assignDialog.newName}
                onChange={(e) => setAssignDialog((d) => ({ ...d, newName: e.target.value }))}
                placeholder="New customer name (e.g. HANDLE-IT INC.)"
                data-testid="pl-assign-new-name"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, row: null, customerId: "", newName: "" })} disabled={assignBusy}>Cancel</Button>
            <Button onClick={submitAssign} disabled={assignBusy || !assignDialog.customerId} data-testid="pl-assign-submit">
              {assignBusy ? "Saving…" : "Save grouping"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SafetyShell>
  );
}

export default PackingLists;
